import { Controller } from 'stimulus'
import differenceWith from 'lodash.differencewith'
import uniq from 'lodash.uniq'
import isEqual from 'lodash.isequal'
import { defaultPropertiesAction } from '../constants/analytics'

// suggested by NLP
let suggested = {}

// selected by user
let selectedSuggestions = {}

/**
 * @namespace ActionFormController
 */
export default class extends Controller {
    documentReferrer;
    formAction;
    formValuesInitial;

    /**
     * Create an array of objects that captures the form's values
     * at certain points in time --namely, connect & submit.
     */
    getFormValues() {
      const formElements = this.element.elements
      const ignoredElements = ['', '_method', 'authenticity_token', 'query_string', 'intl_scope']

      const formValues = []

      // Call the Array forEach method on the form elements collection, thereby treating it as an Array
      // {@link https://developer.mozilla.org/en-US/docs/Web/API/HTMLFormElement/elements}
      Array.prototype.forEach.call(formElements, function(element) {
        const field = {
          name: element.name,
          value: element.value
        }
        const existingObject = formValues.find(formValueObject => formValueObject.name === field.name)
        if (existingObject) {
          // Use the spread operator to combine individual input values into one values array
          // (accommodates inputs with the same name, in the Pills component)
          const existingFieldValue = typeof existingObject.value === 'object' ? existingObject.value : [existingObject.value]
          const additionalFieldValue = field.value
          const combinedFieldValue = [...existingFieldValue, additionalFieldValue]
          // Re-assign value to combined values array
          const existingObjectIndex = formValues.indexOf(existingObject)
          formValues[existingObjectIndex].value = combinedFieldValue
        } else if (!ignoredElements.includes(field.name)) {
          formValues.push(field)
        }
      })

      return formValues
    }

    /**
     * Capture the form's initial values, form's action, and document referrer
     * right when the controller is connected.
     */
    connect() {
      this.documentReferrer = document.referrer
      this.formAction = this.element.action
      this.formValuesInitial = this.getFormValues()
      this.setDefaultAccumulatedSuggestions()
    }

    /**
     * Suggested via NLP
     */
    addSuggestion(type, name, rank, score, value) {
      suggested[type].push({ name: name, rank: rank, score: score, value: value })

      return suggested
    }

    /**
     * NLP Suggestion selected
     */
    suggestionSelected(type, name, value) {
      selectedSuggestions[type].push({ name: name, value: value })

      return selectedSuggestions
    }

    getActionAttendeeNames(formValues) {
      const attendeeIds = formValues.find(formValueObject => formValueObject.name.includes('attendee')).value
      return attendeeIds.map(id => $(`input[name*="attendee"][value="${id}"]`).prevAll('.removable-pill-label').text().trim())
    }

    getActionDatetimeStart(formValues) {
      return formValues.find(formValueObject => formValueObject.name.includes('action_date')).value
    }

    getActionDatetimeEnd(formValues) {
      return formValues.find(formValueObject => formValueObject.name.includes('action_end_date')).value
    }

    getActionIssues(formValues) {
      return this.getActionItems('issue_ids', formValues)
    }

    getActionItems(type, formValues) {
      const formFieldData = formValues.find(formValueObject => formValueObject.name.includes(type))
      let items = []

      if (Array.isArray(formFieldData.value)) {
        items = formFieldData.value.filter(e => e !== '')
      }

      return items
    }

    getActionLabels(formValues) {
      return this.getActionItems('label_ids', formValues)
    }

    getActionTitle(formValues) {
      return formValues.find(formValueObject => formValueObject.name.includes('title')).value
    }

    getActionTypeName(formValues) {
      const typeId = formValues.find(formValueObject => formValueObject.name.includes('type')).value
      return $(`#form__type [data-value="${typeId}"]`).text().trim()
    }

    getActionLinkedItems(formValues) {
      return this.getActionItems('linked_item_ids', formValues)
    }

    setDefaultAccumulatedSuggestions() {
      suggested = {
        issues: [],
        labels: [],
        'linked-items': []
      }
      selectedSuggestions = {
        issues: [],
        labels: [],
        'linked-items': []
      }
    }

    /**
     * When the user creates or duplicates an Action, determine which properties to send to the analytics tracker.
     */
    sendAnalyticsCreate() {
      // Capture the values that were submitted
      const formValuesSubmitted = this.getFormValues()

      // Put the event properties together
      const createProperties = {
        action_attendees: this.getActionAttendeeNames(formValuesSubmitted),
        action_origin_page: this.documentReferrer,
        action_title: this.getActionTitle(formValuesSubmitted),
        action_type: this.getActionTypeName(formValuesSubmitted),
        all_action_properties: formValuesSubmitted,
        date_end: this.getActionDatetimeEnd(formValuesSubmitted),
        date_start: this.getActionDatetimeStart(formValuesSubmitted),
        from_duplicate: this.formAction.includes('duplicate_action'),
        labels: this.getActionLabels(formValuesSubmitted),
        issues: this.getActionIssues(formValuesSubmitted),
        linked_items: this.getActionLinkedItems(formValuesSubmitted),
        service: 'in-app'
      }

      this.sendAnalytics('Action Created', { ...createProperties, ...this.suggestedProperties(formValuesSubmitted) })
    }

    /**
     * When the user edits an Action, determine which properties to send to the analytics tracker.
     * {@link https://lodash.com/docs/4.17.15#differenceWith}
     * {@link https://lodash.com/docs/4.17.15#isEqual}
     */
    sendAnalyticsUpdate() {
      // Pick out only the values that have changed
      const formValuesSubmitted = this.getFormValues()
      const formValuesChanged = differenceWith(formValuesSubmitted, this.formValuesInitial, isEqual)
      // Grab the Action ID
      const formActionSliceIndex = this.formAction.lastIndexOf('/') + 1
      const actionId = this.formAction.slice(formActionSliceIndex)

      // Put the event properties together
      const updateProperties = {
        action_id: actionId,
        action_origin_page: this.documentReferrer,
        action_properties_changed: formValuesChanged,
        action_title: this.getActionTitle(this.formValuesInitial),
        action_type: this.getActionTypeName(formValuesSubmitted),
        date_end: this.getActionDatetimeEnd(formValuesSubmitted),
        date_start: this.getActionDatetimeStart(formValuesSubmitted),
        labels: this.getActionLabels(formValuesSubmitted),
        issues: this.getActionIssues(formValuesSubmitted),
        linked_items: this.getActionLinkedItems(formValuesSubmitted)
      }

      this.sendAnalytics('Action Edited', { ...updateProperties, ...this.suggestedProperties(formValuesSubmitted) })
    }

    /**
     * Properties related to suggested items.
     */
    suggestedProperties(formValuesSubmitted) {
      const issueIds = this.getActionIssues(formValuesSubmitted)
      const labelIds = this.getActionLabels(formValuesSubmitted)
      const linkedItemsIds = this.getActionLinkedItems(formValuesSubmitted)

      return {
        suggestion_model: 'Actions Suggestions V1',
        issues_suggested: suggested.issues.map(x => x.value),
        issues_suggestion_selections: selectedSuggestions.issues.map(x => x.value),
        issues_suggestion_selection_count: selectedSuggestions.issues.length,
        issues_suggestion_results: uniq(suggested.issues.map(x => {
          return {
            'item-id': x.value,
            suggestion_chosen: (issueIds.includes(x.value) ? 1 : 0),
            rank: Number(x.rank),
            score: Number(x.score)
          }
        })),

        labels_suggested: suggested.labels.map(x => x.value),
        labels_suggestion_selections: selectedSuggestions.labels.map(x => x.value),
        labels_suggestion_selection_count: selectedSuggestions.labels.length,
        labels_suggestion_results: uniq(suggested.labels.map(x => {
          return {
            'item-id': x.value,
            suggestion_chosen: (labelIds.includes(x.value) ? 1 : 0),
            rank: Number(x.rank),
            score: Number(x.score)
          }
        })),

        linked_items_suggested: suggested['linked-items'].map(x => x.value),
        linked_items_suggestion_selections: selectedSuggestions['linked-items'].map(x => x.value),
        linked_items_suggestion_selection_count: selectedSuggestions['linked-items'].length,
        linked_items_suggestion_results: uniq(suggested['linked-items'].map(x => {
          return {
            'item-id': x.value,
            suggestion_chosen: (linkedItemsIds.includes(x.value) ? 1 : 0),
            rank: Number(x.rank),
            score: Number(x.score)
          }
        }))
      }
    }

    /**
     * Send data to the analytics tracker.
     */
    sendAnalytics(eventName, eventProperties) {
      const properties = { ...defaultPropertiesAction, ...eventProperties }
      analytics.track(eventName, properties)
    }
}
